import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReviewDetailTabsComponent, ReviewDetailTabsService, ActionNeededFieldChange, ProjectDateChange } from 'review-detail-tabs';
import { exhaustMap, takeUntil } from 'rxjs/operators';
import { ReviewHeaderComponent } from '../review-header/review-header.component';
import { CONSTANTS } from '../../../Constants/Constants';
import { CostReviewAttributes, CostReviewDatabaseRecord, CostReviewElectricalAttributes, CostReviewFireEnUsAttributes, CostReviewMarginExceptions, 
  CostReviewPrefab, CostReviewSecurityEnUsAttributes, CostReviewStatus, CostReviewSummary, CostReviewType, Escalations,TaskBasedLaborSettings } from '../../../models/dtos';
import { CostReviewReasonForExceptionDialog } from '../../dialogs/common/cost-review-reason-for-exception/cost-review-reason-for-exception.component';
import { JobDetails } from '../../dialogs/common/job-details/job-details.component';
import { ICostReviewActionsDialogReturnValue } from '../../../models/reports/ICostReviewActionsDialogReturnValue';
import { ActionNeededDto, ActionNeededType } from '../../../models/common/ActionNeededDto';
import { ActionNeededMappingService } from '../../../services/review/action-needed-mapping/action-needed-mapping.service';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { CommonDataService } from '../../../services/common-data.service';
import { CostReviewRecordBuilderService } from '../../../services/review/cost-review-record-builder.service';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { TranslateService } from '@ngx-translate/core';
import { EscalationResetService } from '../../../services/operations/escalation-reset.service';
import { CostReviewStateSyncService } from '../../../services/review/cost-review-state-sync.service';
import { ProjectManagementHoursUpdateService } from '../../../services/labor/project-management-hours-update.service';

@Component({
  selector: 'app-review-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReviewDetailTabsComponent,
    ReviewHeaderComponent
  ],
  templateUrl: './review-detail.component.html',
  styleUrls: ['./review-detail.component.scss'],
  providers: [ActionNeededMappingService]
})
export class ReviewDetailComponent implements OnInit, OnDestroy {

  @Input() readOnlyView: boolean = false;
  costReviewObject: CostReviewDatabaseRecord = null;
  public actionNeededVisible: boolean = false;
  public actionNeededCollection: ActionNeededDto[] = [];
  private readonly destroy$ = new Subject<void>();

  /** Last dates accepted by the user — the baseline for the escalation reset check and for reverting. */
  private acceptedProjectDates: ProjectDateChange | null = null;
  private taskBasedLaborSettings: TaskBasedLaborSettings | null = null;

  constructor(private readonly dialog: MatDialog,
    private readonly reviewDetailsTabService: ReviewDetailTabsService,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly actionNeededMappingService: ActionNeededMappingService,
    private readonly escalationResetService: EscalationResetService,
    private readonly projectManagementHoursUpdateService: ProjectManagementHoursUpdateService,
    private readonly costReviewRecordBuilderService: CostReviewRecordBuilderService,
    private readonly costReviewStateSyncService: CostReviewStateSyncService,
    private readonly translateService: TranslateService) {
  }

  ngOnInit(): void {
    void this.initializeComponent().catch(error =>{
      console.error('Failed to initialize cost review details', error);
    });
  }
  
  private initializeComponent(): Promise<void> {
    // The header reads costReviewTableData synchronously in its own ngOnInit, which runs in the
    // same change-detection pass as this one — so it must be published before the first await.
    this.costReviewObject = this.commonDataService.getCostReviewData();
    this.costReviewRecordBuilderService.costReviewTableData = this.costReviewObject;
    this.costReviewRecordBuilderService.restoreAutoUnlockMessageFromSavedRecord();
    this.commonDataService.setIsReviewCancelledBySystem = false;

    return this.costReviewStateSyncService.loadCostReviewDetails()
    .then(() => {
      if(this.commonDataService.getBusiness() === CONSTANTS.BUSINESS_FIRE) {
        this.taskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();
        if (this.taskBasedLaborSettings) {
          this.commonDataService.jobDetailsExceptionVisible = this.commonFunctionsService.hasJobDetailsErrors(this.taskBasedLaborSettings);
          this.checkActionNeededVisibility();
        }
      }
      this.costReviewObject = this.commonDataService.getCostReviewData();
      this.costReviewRecordBuilderService.costReviewTableData = this.costReviewObject;
      const parameters = this.commonDataService.getReviewParameterResponse();
      this.reviewDetailsTabService.setJobDetailsProductTypes(this.commonFunctionsService.getFireJobDetailsProductTypes());
      this.reviewDetailsTabService.setReviewParameterData(parameters);
      this.reviewDetailsTabService.setReviewDetailsData(this.costReviewObject);
      this.costReviewRecordBuilderService.reviewerList = this.costReviewObject?.costReviewers || [];
      this.reviewDetailsTabService.setBusiness(this.commonDataService.getBusiness());
      this.reviewDetailsTabService.setEstimateSellPrice(this.commonDataService.getEstimateSoldAsSellPrice());
      this.acceptedProjectDates = this.captureProjectDates();
      this.applyReadOnlyBasedOnStatus();
      this.checkActionNeededVisibility();
      this.handleAllSubscriptions();
    });
  }

  private handleAllSubscriptions(): void {
    this.subscribeToAppReadOnly();
    this.subscribeToLibraryChanges();
    this.subscribeToProjectDateChanges();
    this.subscribeToUnsavedChanges();
    this.subscribeToTaskBasedLaborSettings();
    this.subscribeToJobDetailsDialogRequests();
  }

  private subscribeToJobDetailsDialogRequests(): void {
    this.reviewDetailsTabService.getJobDetailsDialogRequested$
      .pipe(
        exhaustMap(() => this.dialog.open(JobDetails, {
          width: '800px',
          disableClose: true
        }).afterClosed()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private applyReadOnlyBasedOnStatus(): void {
    // Cost-review status → library. Per requirements truth table:
    //   Write mode: NotStarted, Denied
    //   Read  mode: everything else (InProcess, Approved, Pending, None)
    // The final library value is `appFlag OR isStatusReadOnly` and is pushed
    // by `pushLibraryReadOnly()`. Cost-review status is intentionally NOT
    // propagated back into the app flag.
    this.pushLibraryReadOnly();

    //below flag will disable complete review tab according to review status
    const status = this.costReviewObject?.status;
    const isDisabled = status === CostReviewStatus.Approved
      || status === CostReviewStatus.InProcess
      || status === CostReviewStatus.Pending;
    this.reviewDetailsTabService.setReviewLibraryDisableFlag(isDisabled);
  }

  /**
   * Write mode per the cost review status truth table: `NotStarted` and `Denied`, plus the
   * not-yet-resolved status, for which the library is kept editable as well.
   */
  private isCostReviewEditable(): boolean {
    const status = this.costReviewObject?.status;
    return status === undefined || status === null
      || status === CostReviewStatus.NotStarted
      || status === CostReviewStatus.Denied || status === CostReviewStatus.None;
  }

  /**
   * Sole writer for the library's read-only flag. Combines the current app
   * read-only value with the status-driven value and pushes a single value.
   * Called whenever the app flag emits or the review status changes.
   * Reads the status fresh from `costReviewObject` (shared reference) each
   * call — no cached value, so status mutations by the review header are
   * picked up automatically.
   */
  private pushLibraryReadOnly(): void {
    const isStatusReadOnly = !this.isCostReviewEditable();
    const appReadOnly = this.commonDataService.isReadOnlyMode();
    this.readOnlyView = appReadOnly || isStatusReadOnly;
    this.reviewDetailsTabService.setReadOnlyMode(this.readOnlyView);
  }

  /**
   * Subscribes to the app-level read-only flag. Any change (SAP-Submitted,
   * collaboration, header toggle) triggers a re-push of the combined library
   * value. BehaviorSubject emits the current value immediately so this also
   * handles the initial state after `initializeComponent`.
   */
  private subscribeToAppReadOnly(): void {
    this.commonDataService.readOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() =>{ 
        this.pushLibraryReadOnly();
        this.checkActionNeededVisibility();
    });
  }

  /**
   * Flow 2: Library → App
   *
   * Subscribes to changes the library makes to cost-review data.
   * When the library modifies action-needed-relevant fields internally,
   * those changes are synced into the app's `costReviewObject` and
   * action-needed visibility is re-evaluated.
   */
  private subscribeToLibraryChanges(): void {
    this.reviewDetailsTabService.getActionNeededFieldChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe((change: ActionNeededFieldChange) => {
        if (!change || !this.costReviewObject) {
          return;
        }
        // Job details are Fire-only, so their absence must not stop the field
        // change from being applied for the other businesses.
        const taskBasedLaborSettings = this.commonDataService.getTaskBasedLaborSettings();
        if (taskBasedLaborSettings) {
          this.taskBasedLaborSettings = taskBasedLaborSettings;
          this.commonDataService.jobDetailsExceptionVisible = this.commonFunctionsService.hasJobDetailsErrors(taskBasedLaborSettings);
        }
        this.applySingleFieldChange(change);
        this.checkActionNeededVisibility();
        this.reviewDetailsTabService.setReviewDetailsData(this.costReviewObject);
      });
  }

  /**
   * Independent subscription for project date changes from the library.
   * In write mode, propagates dates to all app-wide data sources so reports, dialogs,
   * and other tabs reflect the updated values.
   */
  private subscribeToProjectDateChanges() {
    this.reviewDetailsTabService.getProjectDateChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe((dates: ProjectDateChange) => {
        this.handleProjectDateChange(dates)
          .catch(error => console.error('Failed to handle project date change:', error));
      });
  }

  /**
   * Escalation is spread per financial year, so dates that move into another financial year
   * invalidate the current spread. The user has to accept the reset before the new dates are
   * propagated; declining restores the dates the review tab was showing before the edit.
   */
  private async handleProjectDateChange(dates: ProjectDateChange): Promise<void> {
    // Use the same status and app-mode checks that determine the library's write mode.
    if (!this.isCostReviewEditable() || this.commonDataService.isReadOnlyMode()) {
      return;
    }

    const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;

    const isResetRequired = this.escalationResetService.isEscalationResetRequiredForDateChange(
      this.acceptedProjectDates?.projectStartDate ?? null,
      this.acceptedProjectDates?.projectEndDate ?? null,
      dates.projectStartDate,
      dates.projectEndDate
    );

    if (isResetRequired) {
      const needsConfirmation = this.escalationResetService.isEscalationConfirmationRequired(
        dates.projectStartDate,
        dates.projectEndDate
      );
      if (needsConfirmation && !await this.escalationResetService.confirmEscalationResetForDateChange()) {
        this.revertProjectDates();
        return;
      }

      this.stageEscalationReset(escalationRates, dates);
    }

    const previousProjectDates = {
      startDate: this.acceptedProjectDates?.projectStartDate ?? null,
      endDate: this.acceptedProjectDates?.projectEndDate ?? null
    };
    this.syncProjectDates(dates);
    this.acceptedProjectDates = { ...dates };

    // Project management hours are duration driven; their cost cascade has to settle before
    // escalation is re-applied, so escalation builds on the updated labor costs.
    await this.projectManagementHoursUpdateService.updateHoursOnDurationChange(
      dates.projectStartDate,
      dates.projectEndDate,
      previousProjectDates
    );

    if (isResetRequired) {
      this.escalationResetService.isEscalationResetFromReview = true;
      this.escalationResetService.notifyEscalationResetWithoutPatch();
    }
  }

  private stageEscalationReset(escalationRates: Escalations, dates: ProjectDateChange): void {
    this.escalationResetService.resetEscalationForDateChange(
      escalationRates,
      dates.projectStartDate,
      dates.projectEndDate
    );

    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    estimateMetaData.escalationRates = escalationRates;
    this.commonDataService.patchEstimateMetaData(estimateMetaData);
  }

  private captureProjectDates(): ProjectDateChange {
    const summary = this.costReviewObject?.reviewObject?.costReviewSummary;
    return {
      projectStartDate: summary?.projectStartDate ?? null,
      projectEndDate: summary?.projectEndDate ?? null,
      projectDuration: summary?.projectDuration ?? CONSTANTS.ZERO
    };
  }

  private revertProjectDates(): void {
    const summary = this.costReviewObject?.reviewObject?.costReviewSummary;
    if (!this.acceptedProjectDates || !summary) {
      return;
    }

    summary.projectStartDate = this.acceptedProjectDates.projectStartDate;
    summary.projectEndDate = this.acceptedProjectDates.projectEndDate;
    summary.projectDuration = this.acceptedProjectDates.projectDuration;

    // Re-emitting the record makes the summary tab rebuild its date controls from the restored values.
    this.reviewDetailsTabService.setReviewDetailsData(this.costReviewObject);
  }

  private subscribeToUnsavedChanges(): void {
    this.reviewDetailsTabService.getHasUnsavedChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasChanges: boolean) => {
        if (hasChanges || !this.commonDataService.isSaveEnabled) {
          this.commonDataService.isSaveEnabled = hasChanges;
        }
      });
  }

  /**
   * Two-way bridge for task-based labor settings (incl. job details) between the
   * app's CommonDataService and the review-detail library.
   *
   * App -> Library must be wired first so the library's BehaviorSubject is seeded
   * with the current app value before the reverse subscription replays it back.
   * Both services short-circuit on reference equality, which stops the echo loop.
   */
  private subscribeToTaskBasedLaborSettings(): void {
    this.commonDataService.taskBasedLaborSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.reviewDetailsTabService.setTaskBasedLaborSettings(settings);
      });

    this.reviewDetailsTabService.taskBasedLaborSettings$
    .pipe(takeUntil(this.destroy$))
    .subscribe((settings) => {
      if (!settings) {
        return;
      }
      this.taskBasedLaborSettings = settings;
      this.commonDataService.setTaskBasedLaborSettings(settings);
      this.commonDataService.jobDetailsExceptionVisible = this.commonFunctionsService.hasJobDetailsErrors(settings);
      this.checkActionNeededVisibility();
      });
  }

  private syncProjectDates(dates: ProjectDateChange): void {
    // 1. Update costReviewSummary on the shared object
    const summary = this.costReviewObject?.reviewObject?.costReviewSummary;
    if (summary) {
      summary.projectStartDate = dates.projectStartDate;
      summary.projectEndDate = dates.projectEndDate;
      summary.projectDuration = dates.projectDuration;
    }

    // 2. Update estimateData (consumed by PPR dialog, SAP submittal)
    const estimateData = this.commonDataService.getEstimateData();
    if (estimateData) {
      estimateData.startDate = dates.projectStartDate;
      estimateData.endDate = dates.projectEndDate;
    }

    // 3. Update report header dates (consumed by all PDF/checklist reports)
    this.commonDataService.updateReportDates(dates.projectStartDate, dates.projectEndDate);
  }

  /**
   * Routes a single field change to the correct business-specific mapper
   * based on the current cost review type.
   */
  private applySingleFieldChange(change: ActionNeededFieldChange): void {
    switch (this.costReviewObject?.newReviewObject?.costReviewType) {
      case CostReviewType.AutomationEnUs:
      case CostReviewType.ElectricalEnUs:
      case CostReviewType.MechanicalEnUs:
        this.actionNeededMappingService.applySingleAutomationFieldChange(change, this.costReviewObject);
        break;
      case CostReviewType.FireEnUs:
        this.actionNeededMappingService.applySingleFireFieldChange(change, this.costReviewObject);
        break;
      case CostReviewType.SecurityEnUs:
        this.actionNeededMappingService.applySingleSecurityFieldChange(change, this.costReviewObject);
        break;
      default:
        break;
    }
  }

  openActionNeededDialog() {
    this.populateBusinessSpecificData();
    const dialogRef = this.dialog.open(CostReviewReasonForExceptionDialog, {
      disableClose: true,
      width: '1000px',
      data: { actionNeededCollection: this.actionNeededCollection }
    });
    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe((result: ICostReviewActionsDialogReturnValue) => {
      if (result) {
        this.actionNeededCollection = result.actionNeededCollection;
        this.assignChangesFromActionNeeded();
        this.reviewDetailsTabService.setReviewDetailsData(this.costReviewObject);
      }
    });
  }

  private populateBusinessSpecificData(): void {
    const segmentId = this.commonDataService.getEstimateData()?.profitCenterId ?? 0;
    switch (this.costReviewObject?.newReviewObject?.costReviewType) {
      case CostReviewType.AutomationEnUs:
      case CostReviewType.MechanicalEnUs:
        this.populateActionNeededAutomation(segmentId);
        break;
      case CostReviewType.ElectricalEnUs:
        this.populateActionNeededElectrical();
        break;
      case CostReviewType.SecurityEnUs:
        this.populateActionNeededSecurity(segmentId);
        break;
      case CostReviewType.FireEnUs:
        this.populateActionNeededFire(segmentId);
        break;
      default:
        //Empty default for sonar comply
        break;
    }
  }

  private assignChangesFromActionNeeded(): void {
    switch (this.costReviewObject?.newReviewObject?.costReviewType) {
      case CostReviewType.AutomationEnUs:
      case CostReviewType.MechanicalEnUs:
        this.assignChangesFromActionNeededAutomation();
        break;
      case CostReviewType.ElectricalEnUs:
        this.assignChangesFromActionNeededElectrical();
        break;
      case CostReviewType.SecurityEnUs:
        this.assignChangesFromActionNeededSecurity();
        break;
      case CostReviewType.FireEnUs:
        this.assignChangesFromActionNeededFire();
        break;
      default:
        //Empty default for sonar comply
        break;
    }
  }

  /** Mirrors `string.IsNullOrWhiteSpace` so populate and button-status checks agree. */
  private isNullOrWhiteSpace(value: string | null | undefined): boolean {
    return !value?.trim();
  }

  private checkActionNeededVisibility(): void {
    this.actionNeededVisible = false;
    if (this.costReviewObject?.status === CostReviewStatus.Approved ||
        this.costReviewObject?.status === CostReviewStatus.InProcess ||
        this.costReviewObject?.status === CostReviewStatus.Pending)
    {
        return;
    }
    switch (this.costReviewObject?.newReviewObject?.costReviewType) {
      case CostReviewType.AutomationEnUs:
      case CostReviewType.MechanicalEnUs:
        this.updateActionNeededButtonStatusAutomation();
        break;
      case CostReviewType.ElectricalEnUs:
        this.updateActionNeededButtonStatusElectrical();
        break;
      case CostReviewType.FireEnUs:
        this.updateActionNeededButtonStatusFire();
        break;
      case CostReviewType.SecurityEnUs:
        this.updateActionNeededButtonStatusSecurity();
        break;
      default:
        this.actionNeededVisible = false;
    }
  }

  //#region Automation / Electrical / Mechanical

  /**
   * Checks whether the given segment belongs to the
   * Automation / Electrical / Mechanical group.
   *
   * Matching segment IDs:
   *   Automation  – 2, 12, 19, 20
   *   Electrical  – 48, 49
   *   Mechanical  – 50, 51
   */
  private checkSegmentForAutomation(segmentId: number): boolean {
    return segmentId === CONSTANTS.TWO || 
      segmentId === CONSTANTS.TWELVE ||
      this.commonFunctionsService.isBPSEnergyServicesSegmentId(segmentId) || 
      this.commonFunctionsService.isBPSEnergyServicesIoTSegmentID(segmentId) ||
      segmentId === CONSTANTS.FIFTY ||
      segmentId === CONSTANTS.FIFTY_ONE || 
      segmentId === CONSTANTS.ELEVEN ||
      segmentId === CONSTANTS.FORTY_EIGHT ||
      segmentId === CONSTANTS.FORTY_NINE;
  }

  private isAutomationSegmentVisible(segmentId: number): boolean {
    return !(
      segmentId === CONSTANTS.FORTY_EIGHT ||
      segmentId === CONSTANTS.FORTY_NINE || 
      segmentId === CONSTANTS.FIFTY ||
      segmentId === CONSTANTS.FIFTY_ONE
    );
  }

  private isElectricalSegmentVisible(segmentId: number): boolean {
    return (
      segmentId === CONSTANTS.FORTY_EIGHT ||
      segmentId === CONSTANTS.FORTY_NINE
    );
  }

  private isMechanicalSegmentVisible(segmentId: number): boolean {
    return (
      segmentId === CONSTANTS.FIFTY ||
      segmentId === CONSTANTS.FIFTY_ONE
    );
  }

  /**
   * Builds the ordered `actionNeededCollection` for Cost-Review types
   * Automation (1), Electrical (5), and Mechanical (6).
   * @param segmentId  The estimate's segment identifier.
   */
  private populateActionNeededAutomation(segmentId: number): void {
    this.actionNeededCollection = [];
    if (!this.costReviewObject?.reviewObject) {
      return;
    }
    const costReviewAttributes: CostReviewAttributes = this.costReviewObject.reviewObject as CostReviewAttributes;
    const marginExceptions: CostReviewMarginExceptions = costReviewAttributes.costReviewMarginExceptions;
    if (this.checkSegmentForAutomation(segmentId)
      && marginExceptions.isReasonForExceptionApplicable) {
      // 1. Margin SM/BGM Approval
      if (marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_APPROVAL_RECEIVED'),
          type: ActionNeededType.MarginSmBgmApproval,
          isVisible: true,
          vpOrBgmApprovalVisible: true,
          radioButtonGroupName: 'AutomationSmOrBgmApproval',
          isAction: false,
        });
      }
      // 2. Approver Email (Margin)
      if (this.isNullOrWhiteSpace(marginExceptions.approverEmailId)) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_APPROVER_EMAIL'),
          type: ActionNeededType.MarginApproverEmail,
          isVisible: true,
          isApproverEmailTextboxVisible: true,
          actionTextValue: '',
        });
      }
      // 3. Reason For Exception (Margin)
      if (this.isNullOrWhiteSpace(marginExceptions.reasonForException)) {
        this.populateAutomationReasonForException(segmentId);
      }
    }
    // 4. Escalation SM/BGM Approval
    if (marginExceptions.isBGMApprovalApplicable
      && marginExceptions.smApprovalReceived === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ESCALATION_APPROVAL_RECEIVED'),
        type: ActionNeededType.EscalationSmBgmApproval,
        isVisible: true,
        vpOrBgmApprovalVisible: true,
        radioButtonGroupName: 'AutomationEscalationSmOrBgmApproval',
        isAction: false,
      });
    }
    // 5. Engineer Exception items (3 items)
    this.populateActionNeededForEngineerException(costReviewAttributes);
    // 6. Prefab Exception (1 item)
    this.populateActionNeededForPrefabException(costReviewAttributes.costReviewPrefab);
  }

  /**
   * Populates up to 3 action-needed items for Engineer Exceptions:
   *   5a. Engineer Approval        – when IsEngineerExceptionApplicable && EngineerApprovalReceived === 2
   *   5b. Engineer Approver Email   – when IsEngineerExceptionApplicable && EngineerApproverEmail is empty
   *   5c. Engineer Reason For Exception – when IsEngineerExceptionApplicable && EngineerReasonForException is empty
   */
  private populateActionNeededForEngineerException(costReviewAttributes: CostReviewAttributes): void {
    if (!costReviewAttributes.isEngineerExceptionApplicable) {
      return;
    }
    // 5a. Engineer Approval (approval denied / not received)
    if (costReviewAttributes.engineerApprovalReceived === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ENGINEER_TDC_APPROVAL_RECEIVED'),
        type: ActionNeededType.EngineerApproval,
        isVisible: true,
        isEngineerApprovalVisible: true,
        isEngineerApproval: false,
      });
    }
    // 5b. Engineer Approver Email (missing)
    if (this.isNullOrWhiteSpace(costReviewAttributes.engineerApproverEmail)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ENGINEER_TDC_APPROVER_EMAIL'),
        type: ActionNeededType.EngineerApproverEmail,
        isVisible: true,
        isEngineerApproverEmailTextboxVisible: true,
        actionTextValue: '',
      });
    }
    // 5c. Engineer Reason For Exception (missing)
    if (this.isNullOrWhiteSpace(costReviewAttributes.engineerReasonForException)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ENGINEER_TDC_REASON_FOR_EXCEPTION'),
        type: ActionNeededType.EngineerReasonForException,
        isVisible: true,
        isEngineerReasonForExceptionComboboxVisible: true,
        actionTextValue: '',
      });
    }
  }

  /**
   * Populates 1 action-needed item for Prefab Exception:
   *   6. Prefab Reason For Exception – when IsPrefabControllerVisible
   *                                      && IsReasonForExceptionVisible
   *                                      && ReasonForException is empty
   */
  private populateActionNeededForPrefabException(prefab: CostReviewPrefab): void {
    if (!prefab) {
      return;
    }
    if (prefab.isPrefabControllerVisible
      && prefab.isReasonForExceptionVisible
      && this.isNullOrWhiteSpace(prefab.reasonForException)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('PREFAB_REASON_FOR_EXCEPTION'),
        type: ActionNeededType.PrefabReasonForException,
        isVisible: true,
        isPrefabReasonForExceptionComboboxVisible: true,
        actionTextValue: '',
      });
    }
  }

  /**
   * Pushes a single Reason For Exception item with the correct combobox
   * visibility flag based on the segment family.
   */
  private populateAutomationReasonForException(segmentId: number): void {
    if (this.isAutomationSegmentVisible(segmentId)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('MARGIN_REASON_FOR_EXCEPTION'),
        type: ActionNeededType.MarginReasonForException,
        isVisible: true,
        isAutomationComboboxVisible: true,
        actionTextValue: '',
      });
    } else if (this.isElectricalSegmentVisible(segmentId)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('MARGIN_REASON_FOR_EXCEPTION'),
        type: ActionNeededType.MarginReasonForException,
        isVisible: true,
        isElectricalComboboxVisible: true,
        actionTextValue: '',
      });
    } else if (this.isMechanicalSegmentVisible(segmentId)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('MARGIN_REASON_FOR_EXCEPTION'),
        type: ActionNeededType.MarginReasonForException,
        isVisible: true,
        isMechanicalComboboxVisible: true,
        actionTextValue: '',
      });
    }
  }

  // ---------------------------------------------------------------------------
  //  AssignChangesFromActionNeeded – Automation / Electrical / Mechanical
  // ---------------------------------------------------------------------------

  /**
   * Processes the `actionNeededCollection` after the user clicks OK in the
   * Action Needed dialog, writing each item's value back into the
   * `CostReviewAttributes` model.
   *
   * Mapping table:
   * | Condition                                             | Source          | Target                                          |
   * |-------------------------------------------------------|-----------------|--------------------------------------------------|
   * | isAutomation/Electrical/MechanicalComboboxVisible     | actionTextValue | marginExceptions.reasonForException              |
   * | isApproverEmailTextboxVisible                         | actionTextValue | marginExceptions.approverEmailId                 |
   * | isAction && type == MarginSmBgmApproval               | convert to 1    | marginExceptions.smOrBgmApprovalRecieved = 1     |
   * | isAction && type == EscalationSmBgmApproval           | convert to 1    | marginExceptions.smApprovalReceived = 1          |
   * | isEngineerApproval && type == EngineerApproval        | convert to 1    | costReviewAttributes.engineerApprovalReceived = 1|
   * | isEngineerApproverEmailTextboxVisible                 | actionTextValue | costReviewAttributes.engineerApproverEmail       |
   * | isEngineerReasonForExceptionComboboxVisible           | actionTextValue | costReviewAttributes.engineerReasonForException  |
   * | isPrefabReasonForExceptionComboboxVisible             | actionTextValue | costReviewPrefab.reasonForException              |
   */
  private assignChangesFromActionNeededAutomation(): void {
    if (!this.costReviewObject?.reviewObject) {
      return;
    }

    // Single-call mapping: library ActionNeeded[] → app CostReviewAttributes
    this.actionNeededMappingService.applyAllAutomationActionNeeded(
      this.actionNeededCollection,
      this.costReviewObject.reviewObject as CostReviewAttributes
    );

    this.checkActionNeededVisibility();
  }

  // ---------------------------------------------------------------------------
  //  UpdateActionNeededButtonStatus – Automation / Electrical / Mechanical
  // ---------------------------------------------------------------------------

  /**
   * Re-evaluates whether the Action Needed button should be visible after
   * changes have been applied for Automation cost review.
   */
  private updateActionNeededButtonStatusAutomation(): void {
    if (!this.costReviewObject?.reviewObject) {
      this.actionNeededVisible = false;
      return;
    }

    const costReview: CostReviewAttributes = this.costReviewObject.reviewObject as CostReviewAttributes;
    const marginExceptions: CostReviewMarginExceptions = costReview.costReviewMarginExceptions;
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    const isSystemOrSalesTypeVisible = !!(estimateSettings?.isSystemTypeVisible || estimateSettings?.isSalesTypeVisible);

    let actionNeeded = false;

    actionNeeded = isSystemOrSalesTypeVisible &&
        marginExceptions.isReasonForExceptionApplicable &&
        (!marginExceptions.reasonForException?.trim() ||
         marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO ||
         !marginExceptions.approverEmailId?.trim());

    // Escalation approval check
    actionNeeded = actionNeeded || 
        (marginExceptions.isBGMApprovalApplicable && 
         marginExceptions.smApprovalReceived === CONSTANTS.TWO);

    // Engineer exception check
    actionNeeded = actionNeeded || 
        (costReview.isEngineerExceptionApplicable &&
         (costReview.engineerApprovalReceived === CONSTANTS.TWO ||
          !costReview.engineerApproverEmail?.trim() ||
          !costReview.engineerReasonForException?.trim()));

    // Prefab check
    const prefab = costReview.costReviewPrefab;
    actionNeeded = actionNeeded || 
        (prefab?.isPrefabControllerVisible && 
         prefab?.isReasonForExceptionVisible &&
         !prefab?.reasonForException?.trim());

    this.actionNeededVisible = actionNeeded && !this.isEstimateReadonly();  
}

  //endregion

  //#region Electrical

  /**
   * Builds the ordered `actionNeededCollection` for Cost-Review type Electrical (5).
   *
   * Electrical has no segment gate, always uses the Electrical reason-for-exception
   * combobox, and never raises Engineer or Prefab items.
   */
  private populateActionNeededElectrical(): void {
    this.actionNeededCollection = [];
    if (!this.costReviewObject?.reviewObject) {
      return;
    }
    const electricalAttributes: CostReviewElectricalAttributes = this.costReviewObject.reviewObject as CostReviewElectricalAttributes;
    const marginExceptions: CostReviewMarginExceptions = electricalAttributes.costReviewMarginExceptions;
    if (marginExceptions.isReasonForExceptionApplicable) {
      // 1. Margin SM/BGM Approval
      if (marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_APPROVAL_RECEIVED'),
          type: ActionNeededType.MarginSmBgmApproval,
          isVisible: true,
          vpOrBgmApprovalVisible: true,
          radioButtonGroupName: 'AutomationSmOrBgmApproval',
          isAction: false,
        });
      }
      // 2. Approver Email (Margin)
      if (this.isNullOrWhiteSpace(marginExceptions.approverEmailId)) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_APPROVER_EMAIL'),
          type: ActionNeededType.MarginApproverEmail,
          isVisible: true,
          isApproverEmailTextboxVisible: true,
          actionTextValue: '',
        });
      }
      // 3. Reason For Exception (Margin)
      if (this.isNullOrWhiteSpace(marginExceptions.reasonForException)) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_REASON_FOR_EXCEPTION'),
          type: ActionNeededType.MarginReasonForException,
          isVisible: true,
          isElectricalComboboxVisible: true,
          actionTextValue: '',
        });
      }
    }
    // 4. Escalation SM/BGM Approval
    if (marginExceptions.isBGMApprovalApplicable
      && marginExceptions.smApprovalReceived === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ESCALATION_APPROVAL_RECEIVED'),
        type: ActionNeededType.EscalationSmBgmApproval,
        isVisible: true,
        vpOrBgmApprovalVisible: true,
        radioButtonGroupName: 'AutomationEscalationSmOrBgmApproval',
        isAction: false,
      });
    }
  }

  /**
   * Processes the `actionNeededCollection` after the user clicks OK in the
   * Action Needed dialog, writing each item's value back into the
   * `CostReviewElectricalAttributes` model.
   */
  private assignChangesFromActionNeededElectrical(): void {
    if (!this.costReviewObject?.reviewObject) {
      return;
    }

    this.actionNeededMappingService.applyAllElectricalActionNeeded(
      this.actionNeededCollection,
      this.costReviewObject.reviewObject as CostReviewElectricalAttributes
    );

    this.checkActionNeededVisibility();
  }

  /**
   * Re-evaluates whether the Action Needed button should be visible after
   * changes have been applied for Electrical cost review.
   */
  private updateActionNeededButtonStatusElectrical(): void {
    if (!this.costReviewObject?.reviewObject) {
      this.actionNeededVisible = false;
      return;
    }

    const electricalAttributes: CostReviewElectricalAttributes = this.costReviewObject.reviewObject as CostReviewElectricalAttributes;
    const marginExceptions: CostReviewMarginExceptions = electricalAttributes.costReviewMarginExceptions;
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    const isSystemOrSalesTypeVisible = !!(estimateSettings?.isSystemTypeVisible || estimateSettings?.isSalesTypeVisible);

    let actionNeeded = isSystemOrSalesTypeVisible &&
      marginExceptions.isReasonForExceptionApplicable &&
      (!marginExceptions.reasonForException?.trim() ||
       marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO ||
       !marginExceptions.approverEmailId?.trim());

    actionNeeded = actionNeeded ||
      (marginExceptions.isBGMApprovalApplicable &&
       marginExceptions.smApprovalReceived === CONSTANTS.TWO);

    this.actionNeededVisible = actionNeeded && !this.isEstimateReadonly();
  }

  //endregion

  //#region Fire

  // ---------------------------------------------------------------------------
  //  PopulateActionNeeded – Fire
  // ---------------------------------------------------------------------------

  /**
   * Checks whether the given segment belongs to the
   * Fire Service group (full margin exception block).
   *
   * Matching segment IDs:
   *   Fire Services US  – 25
   *   Fire Services CA  – 26
   *   Multi-discipline  – 51
   *
   * Segment 22 (Fire Solutions) returns `false` → solution project path.
   */
  private checkSegmentForFire(segmentId: number): boolean {
    return segmentId === CONSTANTS.TWENTY_FIVE
        || segmentId === CONSTANTS.TWENTY_SIX
        || segmentId === CONSTANTS.FIFTY_ONE
        || segmentId === CONSTANTS.TWENTY_TWO;
  }

  /**
   * Builds the ordered `actionNeededCollection` for Cost-Review type Fire.
   * @param segmentId  The estimate's segment identifier.
   */
  private populateActionNeededFire(segmentId: number): void {
    this.actionNeededCollection = [];
    if (!this.costReviewObject?.reviewObject) {
      return;
    }
    const fireAttributes: CostReviewFireEnUsAttributes = (this.costReviewObject.reviewObject as CostReviewFireEnUsAttributes);
    const marginExceptions: CostReviewMarginExceptions = fireAttributes.costReviewMarginExceptions;
    const costReviewSummary: CostReviewSummary = fireAttributes.costReviewSummary;
    if (this.checkSegmentForFire(segmentId)) {
      // Service project path (segments 25, 26, 51) – populate all 4 service-specific items
      this.populateFireServiceProjectItems(marginExceptions, costReviewSummary);
    } else {
      this.populateFireSolutionProjectItems(costReviewSummary);
    }
    this.populateFireCommonItems(marginExceptions, costReviewSummary);
  }

  /**
   * Populates action-needed items specific to Fire Solution projects
   * (segment 22): ZTE Approval only.
   */
  private populateFireSolutionProjectItems(costReviewSummary: CostReviewSummary): void {
    if (costReviewSummary.isZteApprovalVisible
      && costReviewSummary.zteApprovalReceived === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ZTE_APPROVAL_RECEIVED'),
        type: ActionNeededType.FireZteApproval,
        isVisible: true,
        isTooltipVisible: true,
        actionTooltip: this.translateService.instant('ZTE_APPROVAL_HELP_TEXT'),
        vpOrBgmApprovalVisible: true,
        radioButtonGroupName: 'ZTE_APPROVAL_RECEIVED',
        isAction: false,
      });
    }
  }

  /**
   * Populates action-needed items specific to Fire Service projects
   * (segments 25, 26, 51):
   *  1. ZTE Approval
   *  2. Margin SM/BGM Approval
   *  3. Approver Email (Margin)
   *  4. Reason For Exception (Margin) – IsFireComboboxVisible
   */
  private populateFireServiceProjectItems(
    marginExceptions: CostReviewMarginExceptions,
    costReviewSummary: CostReviewSummary
  ): void {
    // 1. ZTE Approval
    if (costReviewSummary.isZteApprovalVisible
      && costReviewSummary.zteApprovalReceived === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ZTE_APPROVAL_RECEIVED'),
        type: ActionNeededType.FireZteApproval,
        isVisible: true,
        isTooltipVisible: true,
        actionTooltip: this.translateService.instant('ZTE_APPROVAL_HELP_TEXT'),
        vpOrBgmApprovalVisible: true,
        radioButtonGroupName: 'ZTE_APPROVAL_RECEIVED',
        isAction: false,
      });
    }
    // 2. Margin SM/BGM Approval
    if (marginExceptions.isReasonForExceptionApplicable
      && marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('MARGIN_APPROVAL_RECEIVED'),
        type: ActionNeededType.MarginSmBgmApproval,
        isVisible: true,
        vpOrBgmApprovalVisible: true,
        radioButtonGroupName: 'FireSmOrBgmApproval',
        isAction: false,
      });
    }
    // 3. Approver Email (Margin)
    if (marginExceptions.isReasonForExceptionApplicable
      && this.isNullOrWhiteSpace(marginExceptions.approverEmailId)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('MARGIN_APPROVER_EMAIL'),
        type: ActionNeededType.MarginApproverEmail,
        isVisible: true,
        isApproverEmailTextboxVisible: true,
        actionTextValue: '',
      });
    }
    // 4. Reason For Exception (Margin) – Fire combobox
    if (marginExceptions.isReasonForExceptionApplicable
      && this.isNullOrWhiteSpace(marginExceptions.reasonForException)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('MARGIN_REASON_FOR_EXCEPTION'),
        type: ActionNeededType.MarginReasonForException,
        isVisible: true,
        isFireComboboxVisible: true,
        actionTextValue: '',
      });
    }
  }

  /**
   * Populates action-needed items common to both Fire Service and Solution
   * projects: Escalation SM/BGM Approval and TBE SM Approval.
   */
  private populateFireCommonItems(
    marginExceptions: CostReviewMarginExceptions,
    costReviewSummary: CostReviewSummary
  ): void {
    // 5. Escalation SM/BGM Approval
    if (marginExceptions.isBGMApprovalApplicable
      && marginExceptions.smApprovalReceived === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ESCALATION_APPROVAL_RECEIVED'),
        type: ActionNeededType.EscalationSmBgmApproval,
        isVisible: true,
        vpOrBgmApprovalVisible: true,
        radioButtonGroupName: 'ESCALATION_SM_BGM_APPROVAL',
        isAction: false,
      });
    }
    // 6. TBE SM Approval
    if (costReviewSummary.isTBESMApprovalVisible && costReviewSummary.smApprovalRecieved === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('TBE_SM_APPROVAL_RECEIVED'),
        type: ActionNeededType.FireTbeSmApproval,
        isVisible: true,
        isTooltipVisible: true,
        actionTooltip: this.translateService.instant('TBE_SM_APPROVAL_TOOLTIP'),
        isSMApprovalVisible: true,
        isSMApproval: false,
      });
    }
    // 7. Job Details Required
    if(this.commonDataService.jobDetailsExceptionVisible) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('JOB_DETAILS_REQUIRED_CLICK_HERE_TO_POPULATE'),
        type: ActionNeededType.fireJobDetails,
        isVisible: true,
        isFireJobDetailsVisible: true,
      })
    }
  }

  // ---------------------------------------------------------------------------
  //  AssignChangesFromActionNeeded – Fire
  // ---------------------------------------------------------------------------

  /**
   * Processes the `actionNeededCollection` after the user clicks OK in the
   * Action Needed dialog, writing each item's value back into the
   * `CostReviewFireEnUsAttributes` model.
   *
   * Mapping table:
   * | Condition                                          | Source           | Target                                              |
   * |----------------------------------------------------|------------------|------------------------------------------------------|
   * | isFireComboboxVisible                               | actionTextValue  | marginExceptions.reasonForException                  |
   * | isApproverEmailTextboxVisible                       | actionTextValue  | marginExceptions.approverEmailId                     |
   * | isAction && type matches FireZteApproval            | convert to 1     | costReviewSummary.zteApprovalReceived = 1            |
   * | isAction && type matches MarginSmBgmApproval        | convert to 1     | marginExceptions.smOrBgmApprovalRecieved = 1         |
   * | isAction && type matches EscalationSmBgmApproval    | convert to 1     | marginExceptions.smApprovalReceived = 1              |
   * | isSMApproval && type matches FireTbeSmApproval      | convert to 1     | costReviewSummary.smApprovalRecieved = 1, IsTBEApprovalTaken = true |
   */
  private assignChangesFromActionNeededFire(): void {
    if (!this.costReviewObject?.reviewObject) {
      return;
    }

    const fireAttributes: CostReviewFireEnUsAttributes = this.costReviewObject.reviewObject as CostReviewFireEnUsAttributes;
    const marginExceptions: CostReviewMarginExceptions = fireAttributes.costReviewMarginExceptions;
    const costReviewSummary: CostReviewSummary = fireAttributes.costReviewSummary;

    for (const item of this.actionNeededCollection) {
      this.applyFireTextValues(item, marginExceptions);
      this.applyFireApprovalValues(item, marginExceptions, costReviewSummary);
    }

    // Re-evaluate whether the Action Needed button should remain visible
    this.checkActionNeededVisibility();
  }

  /**
   * Applies text-based values (combobox / textbox) from a single action-needed
   * item back into the Fire margin-exceptions model.
   */
  private applyFireTextValues(item: ActionNeededDto, marginExceptions: CostReviewMarginExceptions): void {
    if (item.isFireComboboxVisible) {
      marginExceptions.reasonForException = item.actionTextValue ?? '';
    }
    if (item.isApproverEmailTextboxVisible) {
      marginExceptions.approverEmailId = item.actionTextValue ?? '';
    }
  }

  /**
   * Applies approval radio-button values from a single action-needed item
   * back into the Fire margin-exceptions / summary models.
   */
  private applyFireApprovalValues(
    item: ActionNeededDto,
    marginExceptions: CostReviewMarginExceptions,
    costReviewSummary: CostReviewSummary
  ): void {
    if (item.isAction) {
      this.applyFireRadioApproval(item, marginExceptions, costReviewSummary);
    }
    if (item.isSMApproval && item.type === ActionNeededType.FireTbeSmApproval) {
      costReviewSummary.smApprovalRecieved = CONSTANTS.ONE;
      costReviewSummary.isTBEApprovalTaken = true;
    }
  }

  /**
   * Maps a radio-button approval (isAction === true) to the correct model field
   * based on the item type.
   */
  private applyFireRadioApproval(
    item: ActionNeededDto,
    marginExceptions: CostReviewMarginExceptions,
    costReviewSummary: CostReviewSummary
  ): void {
    switch (item.type) {
      case ActionNeededType.FireZteApproval:
        costReviewSummary.zteApprovalReceived = CONSTANTS.ONE;
        break;
      case ActionNeededType.MarginSmBgmApproval:
        marginExceptions.smOrBgmApprovalRecieved = CONSTANTS.ONE;
        break;
      case ActionNeededType.EscalationSmBgmApproval:
        marginExceptions.smApprovalReceived = CONSTANTS.ONE;
        break;
      default:
        // No action for other types
        break;
    }
  }

  // ---------------------------------------------------------------------------
  //  UpdateActionNeededButtonStatus – Fire
  // ---------------------------------------------------------------------------

  /**
   * Re-evaluates whether the Action Needed button should be visible after
   * changes have been applied for Fire cost review.
   *
   * The button is hidden when ALL of the following are satisfied:
   *  - ZteApprovalReceived === 1 (Yes) OR IsZteApprovalVisible === false
   *  - SmOrBgmApprovalRecieved === 1 (Yes) OR IsReasonForExceptionApplicable === false
   *  - ApproverEmailId is not empty (if applicable)
   *  - ReasonForException is not empty (if applicable)
   *  - SmApprovalReceived === 1 (Yes) OR IsBGMApprovalApplicable === false
   *  - SmApprovalRecieved (TBE) === 1 (Yes) OR IsFireTBEVisibility === false
   *  - JobDetailsExceptionVisible === false (Job details are not required)
   */
   private updateActionNeededButtonStatusFire(): void {
    if (!this.costReviewObject?.reviewObject) {
      this.actionNeededVisible = false;
      return;
    }
    const fireAttributes: CostReviewFireEnUsAttributes = this.costReviewObject.reviewObject as CostReviewFireEnUsAttributes;
    const marginExceptions: CostReviewMarginExceptions = fireAttributes.costReviewMarginExceptions;
    const costReviewSummary: CostReviewSummary = fireAttributes.costReviewSummary;

    // ZTE approval satisfied?
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    const isSystemOrSalesTypeVisible = !!(estimateSettings?.isSystemTypeVisible || estimateSettings?.isSalesTypeVisible);

    let actionNeeded = false;
    actionNeeded = isSystemOrSalesTypeVisible &&
      marginExceptions.isReasonForExceptionApplicable &&
      (!marginExceptions.reasonForException?.trim() ||
       !marginExceptions.approverEmailId?.trim() ||
       marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO);

    actionNeeded = actionNeeded ||
      (costReviewSummary.isZteApprovalVisible &&
       costReviewSummary.zteApprovalReceived === CONSTANTS.TWO);

    actionNeeded = actionNeeded ||
      (marginExceptions.isBGMApprovalApplicable &&
       marginExceptions.smApprovalReceived === CONSTANTS.TWO);

    actionNeeded = actionNeeded ||
      (costReviewSummary.isTBESMApprovalVisible &&
       costReviewSummary.smApprovalRecieved === CONSTANTS.TWO);

    actionNeeded = actionNeeded || this.commonDataService.jobDetailsExceptionVisible;
    this.actionNeededVisible = actionNeeded && !this.isEstimateReadonly();
  }


  //endregion

  //#region Security

  // ---------------------------------------------------------------------------
  //  PopulateActionNeeded – Security
  // ---------------------------------------------------------------------------

  /**
   * Checks whether the given segment belongs to the Security group.
   *
   * Matching segment IDs:
   *   Security US        – 27
   *   Security CA        – 28
   *   Security Solutions – 24
   *   Multi-discipline   – 51
   */
  private checkSegmentForSecurity(segmentId: number): boolean {
    return segmentId === CONSTANTS.TWENTY_SEVEN
        || segmentId === CONSTANTS.TWENTY_EIGHT
        || segmentId === CONSTANTS.TWENTY_FOUR
        || segmentId === CONSTANTS.FIFTY_ONE;
  }

  /**
   * Builds the ordered `actionNeededCollection` for Cost-Review type Security.
   * @param segmentId  The estimate's segment identifier.
   */
  private populateActionNeededSecurity(segmentId: number): void {
    this.actionNeededCollection = [];
    if (!this.costReviewObject?.reviewObject) {
      return;
    }
    const securityAttributes: CostReviewSecurityEnUsAttributes = this.costReviewObject.reviewObject as CostReviewSecurityEnUsAttributes;
    const marginExceptions: CostReviewMarginExceptions = securityAttributes.costReviewMarginExceptions;
    if (this.checkSegmentForSecurity(segmentId) && marginExceptions.isReasonForExceptionApplicable) {
      // 1. Margin SM/BGM Approval
      if (marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_APPROVAL_RECEIVED'),
          type: ActionNeededType.SecurityMarginSmBgmApproval,
          isVisible: true,
          vpOrBgmApprovalVisible: true,
          radioButtonGroupName: 'SecuritySmOrBgmApproval',
          isAction: false,
        });
      }
      // 2. Approver Email (Margin)
      if (this.isNullOrWhiteSpace(marginExceptions.approverEmailId)) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_APPROVER_EMAIL'),
          type: ActionNeededType.SecurityMarginApproverEmail,
          isVisible: true,
          isApproverEmailTextboxVisible: true,
          actionTextValue: '',
        });
      }
      // 3. Reason For Exception (Margin)
      if (this.isNullOrWhiteSpace(marginExceptions.reasonForException)) {
        this.actionNeededCollection.push({
          label: this.translateService.instant('MARGIN_REASON_FOR_EXCEPTION'),
          type: ActionNeededType.SecurityMarginReasonForException,
          isVisible: true,
          isSecurityComboboxVisible: true,
          actionTextValue: '',
        });
      }
    }
    // 4. Escalation SM/BGM Approval
    if (marginExceptions.isBGMApprovalApplicable
      && marginExceptions.smApprovalReceived === CONSTANTS.TWO) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('ESCALATION_APPROVAL_RECEIVED'),
        type: ActionNeededType.SecurityEscalationSmBgmApproval,
        isVisible: true,
        vpOrBgmApprovalVisible: true,
        radioButtonGroupName: 'SecurityEscaltionSmOrBgmApproval',
        isAction: false,
      });
    }
    // 5. Reason For Labor Variance
    if (securityAttributes.isLaborTDCPercentExceeds
      && (!securityAttributes.reasonForLaborVariance || securityAttributes.reasonForLaborVariance.length === CONSTANTS.ZERO)) {
      this.actionNeededCollection.push({
        label: this.translateService.instant('REASON_FOR_LABOR_VARIANCE'),
        type: ActionNeededType.SecurityReasonForLaborVariance,
        isVisible: true,
        isSecurityReasonForLaborVarianceComboboxVisible: true,
        actionTextValue: '',
      });
    }
  }

  // ---------------------------------------------------------------------------
  //  AssignChangesFromActionNeeded – Security
  // ---------------------------------------------------------------------------

  /**
   * Processes the `actionNeededCollection` after the user clicks OK in the
   * Action Needed dialog, writing each item's value back into the
   * `CostReviewSecurityEnUsAttributes` model.
   *
   * Mapping table:
   * | Condition                                          | Source           | Target                                         |
   * |----------------------------------------------------|------------------|-------------------------------------------------|
   * | isSecurityComboboxVisible                          | actionTextValue  | marginExceptions.reasonForException             |
   * | isSecurityReasonForLaborVarianceComboboxVisible    | actionTextValue  | securityAttributes.reasonForLaborVariance        |
   * | isAction && label matches "Margin SM/BGM"          | convert to 1     | marginExceptions.smOrBgmApprovalRecieved = 1    |
   * | isAction && label matches "Escalation SM/BGM"      | convert to 1     | marginExceptions.smApprovalReceived = 1         |
   * | isApproverEmailTextboxVisible                      | actionTextValue  | marginExceptions.approverEmailId                |
   */
  private assignChangesFromActionNeededSecurity(): void {
    if (!this.costReviewObject?.reviewObject) {
      return;
    }

    const securityAttributes: CostReviewSecurityEnUsAttributes = this.costReviewObject.reviewObject as CostReviewSecurityEnUsAttributes;
    const marginExceptions: CostReviewMarginExceptions = securityAttributes.costReviewMarginExceptions;

    for (const item of this.actionNeededCollection) {
      // Reason For Exception combobox → marginExceptions.reasonForException
      if (item.isSecurityComboboxVisible) {
        marginExceptions.reasonForException = item.actionTextValue ?? '';
      }

      // Reason For Labor Variance combobox → securityAttributes.reasonForLaborVariance
      if (item.isSecurityReasonForLaborVarianceComboboxVisible) {
        securityAttributes.reasonForLaborVariance = item.actionTextValue ?? '';
      }

      // SM/BGM Approval radio buttons
      if (item.isAction) {
        if (item.type === ActionNeededType.SecurityMarginSmBgmApproval) {
          marginExceptions.smOrBgmApprovalRecieved = CONSTANTS.ONE;
        } else if (item.type === ActionNeededType.SecurityEscalationSmBgmApproval) {
          marginExceptions.smApprovalReceived = CONSTANTS.ONE;
        }
      }

      // Approver Email textbox → marginExceptions.approverEmailId
      if (item.isApproverEmailTextboxVisible) {
        marginExceptions.approverEmailId = item.actionTextValue ?? '';
      }
    }

    // Re-evaluate whether the Action Needed button should remain visible
    this.checkActionNeededVisibility();
  }

  // ---------------------------------------------------------------------------
  //  UpdateActionNeededButtonStatus – Security
  // ---------------------------------------------------------------------------

  /**
  * Re-evaluates whether the Action Needed button should be visible after
  * changes have been applied.
   */
  private updateActionNeededButtonStatusSecurity(): void {
    if (!this.costReviewObject?.reviewObject) {
      this.actionNeededVisible = false;
      return;
    }

    const securityAttributes: CostReviewSecurityEnUsAttributes = this.costReviewObject.reviewObject as CostReviewSecurityEnUsAttributes;
    const marginExceptions: CostReviewMarginExceptions = securityAttributes.costReviewMarginExceptions;
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    const isSystemOrSalesTypeVisible = !!(estimateSettings?.isSystemTypeVisible || estimateSettings?.isSalesTypeVisible);

    let actionNeeded = isSystemOrSalesTypeVisible &&
      marginExceptions.isReasonForExceptionApplicable &&
      (!marginExceptions.reasonForException?.trim() ||
       !marginExceptions.approverEmailId?.trim() ||
       marginExceptions.smOrBgmApprovalRecieved === CONSTANTS.TWO);

    actionNeeded = actionNeeded ||
      (marginExceptions.isBGMApprovalApplicable &&
       marginExceptions.smApprovalReceived === CONSTANTS.TWO);

    actionNeeded = actionNeeded || (
      securityAttributes.isLaborTDCPercentExceeds &&
      !securityAttributes.reasonForLaborVariance?.trim());

    this.actionNeededVisible = actionNeeded && !this.isEstimateReadonly();
  }

  private isEstimateReadonly(): boolean {
    return this.commonDataService.isReadOnlyMode() || !this.isCostReviewEditable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Reset component state so a fresh load starts clean
    this.costReviewObject = null;
    this.actionNeededVisible = false;
    this.actionNeededCollection = [];
    this.acceptedProjectDates = null;
  }

}
